import React, { useState, useEffect } from 'react';
import { supabase } from './supabaseClient';
import { useTheme } from './useTheme';
import { useScreen } from './useScreen';

const C = { turquesa:'#5dc1b9', mango:'#FDBE02', coral:'#eb636b', logoGrad:'linear-gradient(135deg, #534AB7, #1D9E75)', triGrad:'linear-gradient(135deg, #5dc1b9, #FDBE02, #eb636b)' };
const font = "'DM Sans', sans-serif";
const serif = "'DM Serif Display', serif";

function Bar() { return <div style={{ width:4, height:24, borderRadius:2, background:C.triGrad, flexShrink:0 }}/>; }

export default function SocialMetrics({ userId, profile }) {
  const t = useTheme();
  const { isMobile } = useScreen();
  const [tab, setTab] = useState('instagram');
  const [history, setHistory] = useState([]);
  const [form, setForm] = useState({ followers:'', avg_likes:'', avg_comments:'', weekly_reach:'', posts_this_week:'' });
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [diagnosis, setDiagnosis] = useState(null);
  const [diagLoading, setDiagLoading] = useState(false);

  useEffect(() => {
    if (!userId) return;
    supabase.from('social_metrics').select('*').eq('user_id', userId).eq('platform', tab).order('week_date', { ascending: false }).limit(8).then(({ data }) => {
      if (data) setHistory(data);
    });
    setDiagnosis(null);
  }, [userId, tab, saved]);

  const saveMetrics = async () => {
    if (!userId || saving) return;
    setSaving(true);
    await supabase.from('social_metrics').insert({
      user_id: userId, platform: tab,
      followers: parseInt(form.followers) || 0,
      avg_likes: parseInt(form.avg_likes) || 0,
      avg_comments: parseInt(form.avg_comments) || 0,
      weekly_reach: parseInt(form.weekly_reach) || 0,
      posts_this_week: parseInt(form.posts_this_week) || 0,
    });
    setForm({ followers:'', avg_likes:'', avg_comments:'', weekly_reach:'', posts_this_week:'' });
    setSaved(s => !s);
    setSaving(false);
  };

  const askDiagnosis = async () => {
    const lastDiag = localStorage.getItem('td_diag_' + userId);
    if (lastDiag && Date.now() - parseInt(lastDiag) < 15 * 24 * 60 * 60 * 1000) {
      setDiagnosis('⏳ Tu próximo diagnóstico estará disponible en unos días. Solo puedes pedirlo cada 15 días.');
      return;
    }
    setDiagLoading(true);
    const cur = history[0];
    const prev = history[1];
    const ctx = cur ? `Plataforma: ${tab}. Esta semana: ${cur.followers} seguidores, ${cur.avg_likes} likes promedio, ${cur.avg_comments} comentarios, ${cur.weekly_reach} alcance, ${cur.posts_this_week} posts.${prev ? ` Semana pasada: ${prev.followers} seguidores, ${prev.avg_likes} likes, ${prev.avg_comments} comentarios, ${prev.weekly_reach} alcance, ${prev.posts_this_week} posts.` : ''}` : 'No hay datos aún.';
    try {
      const { data: { session: smSession } } = await supabase.auth.getSession();
      const r = await fetch('/api/chat', {
        method: 'POST', headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${smSession?.access_token}` },
        body: JSON.stringify({
          messages: [{ role: 'user', content: `Analiza mis métricas de redes sociales como experto y dame recomendaciones específicas:\n${ctx}` }],
          userContext: { userName: profile?.full_name, businessName: profile?.business_name, businessType: profile?.business_type, membership: profile?.membership },
        }),
      });
      const d = await r.json();
      setDiagnosis(d.text || 'No se pudo generar diagnóstico');
      localStorage.setItem('td_diag_' + userId, String(Date.now()));
    } catch (e) { setDiagnosis('Error: ' + e.message); }
    setDiagLoading(false);
  };

  const cur = history[0];
  const prev = history[1];
  const engagement = cur && cur.followers > 0 ? ((cur.avg_likes + cur.avg_comments) / cur.followers * 100).toFixed(1) : null;
  const prevEngagement = prev && prev.followers > 0 ? ((prev.avg_likes + prev.avg_comments) / prev.followers * 100).toFixed(1) : null;
  const diff = (a, b) => b > 0 ? Math.round(((a - b) / b) * 100) : 0;
  const maxFollowers = history.length > 0 ? Math.max(...history.map(h => h.followers), 1) : 1;

  const inp = { width:'100%', padding:'10px 14px', border:`0.5px solid ${t.border}`, borderRadius:12, fontSize:14, fontFamily:font, background:t.card, color:t.text, outline:'none', boxSizing:'border-box' };

  return (
    <section style={{ marginBottom:'2.5rem' }}>
      <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:16 }}><Bar/><h2 style={{ fontFamily:serif, fontSize:isMobile?18:22, fontWeight:400, margin:0 }}>Métricas de redes</h2></div>

      {/* Tabs */}
      <div style={{ display:'flex', gap:6, marginBottom:16 }}>
        {['instagram','tiktok','facebook'].map(p => (
          <button key={p} onClick={() => setTab(p)} style={{ padding:'6px 16px', fontSize:12, borderRadius:50, border:`0.5px solid ${tab===p?C.turquesa:t.border}`, background:tab===p?C.turquesa+'18':'none', color:tab===p?C.turquesa:t.textSecondary, cursor:'pointer', fontFamily:font, fontWeight:500, textTransform:'capitalize' }}>{p==='instagram'?'📸':''}{ p==='tiktok'?'🎵':''}{p==='facebook'?'👍':''} {p}</button>
        ))}
      </div>

      {/* Dashboard if data exists */}
      {cur && (
        <div style={{ marginBottom:20 }}>
          <div style={{ display:'grid', gridTemplateColumns:isMobile?'repeat(2,1fr)':'repeat(4,1fr)', gap:10, marginBottom:16 }}>
            {[
              { label:'Seguidores', val:cur.followers?.toLocaleString(), change:prev?diff(cur.followers,prev.followers):null },
              { label:'Engagement', val:engagement+'%', change:prev&&prevEngagement?diff(parseFloat(engagement),parseFloat(prevEngagement)):null },
              { label:'Alcance', val:(cur.weekly_reach||0).toLocaleString(), change:prev?diff(cur.weekly_reach,prev.weekly_reach):null },
              { label:'Posts/semana', val:cur.posts_this_week, change:prev?diff(cur.posts_this_week,prev.posts_this_week):null },
            ].map((m, i) => (
              <div key={i} style={{ background:t.card, border:`0.5px solid ${t.border}`, borderRadius:16, padding:'14px 12px', textAlign:'center' }}>
                <p style={{ fontSize:11, color:t.textSecondary, margin:'0 0 4px' }}>{m.label}</p>
                <p style={{ fontSize:20, fontWeight:500, margin:'0 0 2px' }}>{m.val}</p>
                {m.change !== null && <p style={{ fontSize:11, fontWeight:500, color:m.change>=0?C.turquesa:C.coral, margin:0 }}>{m.change>=0?'+':''}{m.change}%</p>}
              </div>
            ))}
          </div>

          {/* Mini chart */}
          {history.length > 1 && (
            <div style={{ background:t.card, border:`0.5px solid ${t.border}`, borderRadius:16, padding:'14px', marginBottom:16 }}>
              <p style={{ fontSize:12, fontWeight:500, margin:'0 0 10px' }}>Seguidores últimas semanas</p>
              <div style={{ display:'flex', alignItems:'flex-end', gap:6, height:80 }}>
                {[...history].reverse().map((h, i) => (
                  <div key={i} style={{ flex:1, textAlign:'center' }}>
                    <div style={{ height:`${(h.followers/maxFollowers)*60}px`, background:C.logoGrad, borderRadius:'4px 4px 0 0', minHeight:2, margin:'0 auto', width:'70%' }}/>
                    <p style={{ fontSize:9, color:t.textSecondary, margin:'4px 0 0' }}>S{history.length - i}</p>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Input form */}
      <div style={{ background:t.card, border:`0.5px solid ${t.border}`, borderRadius:20, padding:isMobile?'1rem':'1.25rem', marginBottom:16 }}>
        <p style={{ fontSize:14, fontWeight:500, margin:'0 0 12px' }}>Registrar métricas de esta semana</p>
        <div style={{ display:'grid', gridTemplateColumns:isMobile?'1fr 1fr':'1fr 1fr 1fr', gap:10, marginBottom:12 }}>
          {[{k:'followers',l:'Seguidores'},{k:'avg_likes',l:'Likes promedio'},{k:'avg_comments',l:'Comentarios prom.'},{k:'weekly_reach',l:'Alcance semanal'},{k:'posts_this_week',l:'Posts esta semana'}].map(f => (
            <div key={f.k}>
              <label style={{ fontSize:11, color:t.textSecondary, display:'block', marginBottom:3 }}>{f.l}</label>
              <input type="number" value={form[f.k]} onChange={e => setForm(p => ({...p,[f.k]:e.target.value}))} placeholder="0" style={inp}/>
            </div>
          ))}
        </div>
        <button onClick={saveMetrics} disabled={saving} style={{ background:C.logoGrad, color:'#fff', border:'none', borderRadius:50, padding:'9px 24px', fontSize:13, fontWeight:500, cursor:'pointer', fontFamily:font, opacity:saving?0.6:1 }}>
          {saving ? 'Guardando...' : 'Guardar métricas'}
        </button>
      </div>

      {/* AI Diagnosis */}
      <div style={{ background:t.card, border:`0.5px solid ${t.border}`, borderRadius:20, padding:isMobile?'1rem':'1.25rem' }}>
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:12, flexWrap:'wrap', gap:8 }}>
          <p style={{ fontSize:14, fontWeight:500, margin:0 }}>🧠 Diagnóstico con IA</p>
          <button onClick={askDiagnosis} disabled={diagLoading || !cur} style={{ background:C.logoGrad, color:'#fff', border:'none', borderRadius:50, padding:'8px 20px', fontSize:12, fontWeight:500, cursor:cur?'pointer':'not-allowed', fontFamily:font, opacity:diagLoading||!cur?0.5:1 }}>
            {diagLoading ? 'Analizando...' : 'Pedir diagnóstico'}
          </button>
        </div>
        {diagnosis ? (
          <div style={{ fontSize:14, lineHeight:1.7, whiteSpace:'pre-wrap' }}>{diagnosis}</div>
        ) : (
          <p style={{ fontSize:13, color:t.textSecondary, margin:0 }}>{cur ? 'Presiona para que Genio & Luca analicen tus métricas.' : 'Registra tus métricas primero para pedir un diagnóstico.'}</p>
        )}
      </div>
    </section>
  );
}
